import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useDemo, useAppMode } from '../context/DemoContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { 
  RefreshCw, Edit2, Check, X, ChevronLeft, 
  Coins, Target, Award, Gamepad2, Image,
  Crosshair, Dices, Hammer, Trophy, Share2,
  Shield, Zap, Crown, Gem, Star, Lock, Sparkles, ChevronRight, Type, Users, Clock, Skull, Copy, Link, LogOut, Flame, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProfileBorder, { PROFILE_BORDERS } from '../components/ProfileBorder';
import BackButton from '../components/BackButton';
import { getSolBalance, getTokenBalance, accountExists, playerPda, DEVNET, explorerAddr } from '../lib/solana/devnet';
import { initializePlayerIx, getConnection } from '../lib/solana/devnet';
import { Transaction, SystemProgram } from '@solana/web3.js';
import HomeFooter from '../components/HomeFooter';
import '@fontsource/cinzel/400.css';
import '@fontsource/cinzel-decorative/400.css';
import '@fontsource/cinzel-decorative/700.css';
import '@fontsource/medievalsharp/400.css';
import '@fontsource/share-tech-mono/400.css';
import '@fontsource/orbitron/400.css';
import '@fontsource/orbitron/700.css';

// Genesis passport hero card (MidJourney, webp)
import passportCardArt from '../assets/genesis/passport_card.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_WALLET = 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4';

const ORACLE_FONTS = [
  { id: 'font_whisper', name: 'Whisper', family: 'Cinzel', desc: 'Subtle mysticism', color: '#94a3b8', requirements: { wins: 25, accuracy: 60, streak: 0 } },
  { id: 'font_rune_script', name: 'Rune Script', family: 'MedievalSharp', desc: 'Ancient oracle feel', color: '#a78bfa', requirements: { wins: 75, accuracy: 70, streak: 0 } },
  { id: 'font_void_mono', name: 'Void Mono', family: 'Share Tech Mono', desc: 'Hacker anomaly', color: '#22d3ee', requirements: { wins: 200, accuracy: 80, streak: 0 } },
  { id: 'font_aether_glow', name: 'Aether Glow', family: 'Orbitron', desc: 'Sci-fi oracle', color: '#e879f9', requirements: { wins: 350, accuracy: 85, streak: 15 } },
];

const GREMLIN_LOGO = 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/d97xwjyk_Logo.png';
const SEWER_GATE_BG = 'https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/rhe4nbeu_polyyyyyyyy_A_massive_sewer_gate_cracking_open_with_blinding__89ac3e3f-b5b6-4a3c-8297-cf1308cbd6a8_1.png';

const FEE_MASTER_TITLES = [
  { threshold: 0, title: 'Sewer Rat', color: '#6b7280' },
  { threshold: 1000, title: 'Drain Lurker', color: '#22c55e' },
  { threshold: 5000, title: 'Pipe Runner', color: '#22c55e' },
  { threshold: 15000, title: 'Tunnel Phantom', color: '#3b82f6' },
  { threshold: 35000, title: 'Grime Warden', color: '#3b82f6' },
  { threshold: 75000, title: 'Void Stalker', color: '#a855f7' },
  { threshold: 150000, title: 'Gas Baron', color: '#a855f7' },
  { threshold: 300000, title: 'Sewer Overlord', color: '#f59e0b' },
  { threshold: 500000, title: 'Gremlin Sovereign', color: '#f59e0b' },
  { threshold: 1000000, title: 'Eternal Abomination', color: '#ef4444' },
];

const getFeeMasterTitle = (totalFees) => {
  return [...FEE_MASTER_TITLES].reverse().find(t => totalFees >= t.threshold) || FEE_MASTER_TITLES[0];
};

const GREMLIN_AVATARS = [
  { id: 'gnasher', name: 'Dr. Fizzwick', imageUrl: 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/qqksq8x2_Gremlin-1.png' },
  { id: 'sneaksy', name: 'Skratch', imageUrl: 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/q2q8nn7r_Gremlin%202.png' },
  { id: 'bouncy_blorb', name: 'Grindle Ironpaw', imageUrl: 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/oyefid8i_gremlin%203.png' },
  { id: 'eldraflux', name: 'King Glint', imageUrl: 'https://customer-assets.emergentagent.com/job_8a97e9c9-a75b-4894-af5b-5ab6ea8e8ae3/artifacts/1ala7eao_Gremlin%204.png' },
  { id: 'glitchwick', name: 'Glitchwick', imageUrl: 'https://customer-assets.emergentagent.com/job_1ce44b89-e09f-433a-948a-2b87d4e33580/artifacts/x3k8xagw_Gremlin%205.png' },
  { id: 'vorlix', name: 'Vorlix', imageUrl: 'https://customer-assets.emergentagent.com/job_1ce44b89-e09f-433a-948a-2b87d4e33580/artifacts/xkx12e7m_gremlin%206.png' },
  { id: 'nyxara', name: 'Nyxara', imageUrl: 'https://customer-assets.emergentagent.com/job_1ce44b89-e09f-433a-948a-2b87d4e33580/artifacts/hkklvqd7_gremlin%207.png' },
  { id: 'brakk', name: 'Brakk Ironfang', imageUrl: 'https://customer-assets.emergentagent.com/job_1ce44b89-e09f-433a-948a-2b87d4e33580/artifacts/xizjc4ze_gremlin%208.png' },
];

const TIER_CONFIG = [
  { tier: 'none', name: 'Newcomer', color: '#6b7280', glow: 'rgba(107,114,128,0.3)', holdings: '0', perk: '1.0x SP multiplier', icon: Shield },
  { tier: 'bronze', name: 'Bronze', color: '#cd7f32', glow: 'rgba(205,127,50,0.3)', holdings: '50K', perk: '1.0x SP // +1 bonus roll/mo', icon: Shield },
  { tier: 'silver', name: 'Silver', color: '#c0c0c0', glow: 'rgba(192,192,192,0.3)', holdings: '500K', perk: '1.1x SP // +2 bonus rolls/mo', icon: Star },
  { tier: 'gold', name: 'Gold', color: '#ffd700', glow: 'rgba(255,215,0,0.3)', holdings: '2M', perk: '1.2x SP // +3 bonus rolls/mo', icon: Crown },
  { tier: 'platinum', name: 'Platinum', color: '#00ffff', glow: 'rgba(0,255,255,0.3)', holdings: '5M', perk: '1.4x SP // +5 bonus rolls/mo', icon: Gem },
  { tier: 'diamond', name: 'Diamond', color: '#00bfff', glow: 'rgba(0,191,255,0.3)', holdings: '15M', perk: '1.7x SP // +10 bonus rolls/mo', icon: Sparkles },
  { tier: 'obsidian', name: 'Obsidian', color: '#7c3aed', glow: 'rgba(124,58,237,0.3)', holdings: '50M', perk: '2.0x SP // +15 bonus rolls/mo', icon: Gem },
  { tier: 'mythic', name: 'Mythic', color: '#ff00ff', glow: 'rgba(255,0,255,0.3)', holdings: '150M', perk: '2.5x SP // +25 bonus rolls/mo', icon: Crown },
];

const TIER_STYLES = {
  none: { color: '#6b7280', name: 'Newcomer' },
  bronze: { color: '#cd7f32', name: 'Bronze' },
  silver: { color: '#c0c0c0', name: 'Silver' },
  gold: { color: '#ffd700', name: 'Gold' },
  platinum: { color: '#00ffff', name: 'Platinum' },
  diamond: { color: '#00bfff', name: 'Diamond' },
  obsidian: { color: '#7c3aed', name: 'Obsidian' },
  mythic: { color: '#ff00ff', name: 'Mythic' },
};

const GlassCard = ({ children, className = '', hover = false, accent, ...props }) => null; // deprecated

const Panel = ({ children, className = '', glow = false, style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} {...props}
    style={{
      background: glow ? 'rgba(4,18,8,0.85)' : 'rgba(3,14,6,0.8)',
      border: `1px solid rgba(34,197,94,${glow ? '0.35' : '0.18'})`,
      backdropFilter: 'blur(10px)',
      boxShadow: glow
        ? '0 0 25px rgba(34,197,94,0.1), inset 0 0 20px rgba(34,197,94,0.04)'
        : '0 0 10px rgba(34,197,94,0.03), inset 0 0 10px rgba(34,197,94,0.02)',
      ...style,
    }}>
    <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l z-10" style={{ borderColor: 'rgba(74,222,128,0.4)' }} />
    <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r z-10" style={{ borderColor: 'rgba(74,222,128,0.4)' }} />
    <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l z-10" style={{ borderColor: 'rgba(74,222,128,0.4)' }} />
    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r z-10" style={{ borderColor: 'rgba(74,222,128,0.4)' }} />
    <motion.div className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(34,197,94,0.04) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay, ease: [0.25, 0.1, 0.25, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

// ═══ LIVE BALANCES PANEL — real on-chain devnet balances + PlayerAccount status ═══
const LiveBalancesPanel = ({ walletAddress, Panel }) => {
  const [state, setState] = useState({ loading: true, sol: null, gfg: null, playerExists: null, err: null });
  const [initState, setInitState] = useState({ status: 'idle', err: null, sig: null });
  const { publicKey, signTransaction } = useWallet();

  const refresh = useCallback(async () => {
    if (!walletAddress) return;
    setState(s => ({ ...s, loading: true, err: null }));
    try {
      const [sol, gfg, exists] = await Promise.all([
        getSolBalance(walletAddress),
        getTokenBalance(walletAddress),
        accountExists(playerPda(walletAddress)),
      ]);
      setState({ loading: false, sol, gfg, playerExists: exists, err: null });
    } catch (e) {
      setState(s => ({ ...s, loading: false, err: String(e) }));
    }
  }, [walletAddress]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 18000);
    return () => clearInterval(id);
  }, [refresh]);

  const initializePlayer = useCallback(async () => {
    if (!publicKey || !signTransaction) {
      setInitState({ status: 'error', err: 'Connect Phantom first', sig: null });
      return;
    }
    setInitState({ status: 'signing', err: null, sig: null });
    try {
      const player = playerPda(publicKey);
      const ix = await initializePlayerIx({
        keys: [
          { pubkey: player, isSigner: false, isWritable: true },
          { pubkey: publicKey, isSigner: true, isWritable: true },
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
        ],
      });
      const conn = getConnection();
      const tx = new Transaction().add(ix);
      tx.feePayer = publicKey;
      const { blockhash, lastValidBlockHeight } = await conn.getLatestBlockhash('confirmed');
      tx.recentBlockhash = blockhash;
      const signed = await signTransaction(tx);
      const sig = await conn.sendRawTransaction(signed.serialize(), { skipPreflight: false, preflightCommitment: 'confirmed' });
      setInitState({ status: 'confirming', err: null, sig });
      await conn.confirmTransaction({ signature: sig, blockhash, lastValidBlockHeight }, 'confirmed');
      setInitState({ status: 'success', err: null, sig });
      await refresh();
    } catch (e) {
      const msg = e?.message || String(e);
      const friendly = /User rejected|cancelled|denied/i.test(msg) ? 'Cancelled in wallet' : msg;
      setInitState({ status: 'error', err: friendly, sig: null });
    }
  }, [publicKey, signTransaction, refresh]);

  const playerPdaStr = walletAddress ? playerPda(walletAddress).toBase58() : '';

  return (
    <Panel className="p-4 mb-4" data-testid="profile-live-balances">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2 relative z-10">
        <div className="flex items-center gap-2">
          <span
            className="inline-block rounded-full"
            style={{
              width: 7, height: 7,
              background: '#7dd0ff',
              boxShadow: '0 0 8px #7dd0ff, 0 0 14px rgba(125,208,255,0.55)',
              animation: 'gfg-live-pulse 1.4s ease-in-out infinite',
            }}
          />
          <span
            className="text-[10px] tracking-[0.32em] font-bold uppercase"
            style={{ color: '#a8def7', fontFamily: "'Cinzel', serif" }}
          >
            DEVNET · ON-CHAIN
          </span>
        </div>
        <button
          onClick={refresh}
          disabled={state.loading}
          data-testid="profile-live-refresh"
          className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.18em] hover:brightness-125 transition active:scale-[0.97] disabled:opacity-60"
          style={{
            fontFamily: "'Courier New', monospace",
            color: '#7dd0ff',
            background: 'rgba(8,18,28,0.85)',
            border: '1px solid rgba(125,208,255,0.4)',
          }}
        >
          <RefreshCw className={`w-3 h-3 ${state.loading ? 'animate-spin' : ''}`} />
          REFRESH
        </button>
      </div>

      {state.err && (
        <p className="text-[11px]" style={{ color: '#ff5a4d', fontFamily: "'Courier New', monospace" }}>RPC error: {state.err}</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 relative z-10" style={{ fontFamily: "'Courier New', monospace" }}>
        <div>
          <p className="text-[9px] tracking-[0.22em]" style={{ color: 'rgba(125,208,255,0.55)' }}>SOL BALANCE</p>
          <p className="text-base font-bold" style={{ color: '#a8def7' }}>
            {state.sol == null ? '—' : state.sol.toLocaleString(undefined, { maximumFractionDigits: 4 })}
          </p>
        </div>
        <div>
          <p className="text-[9px] tracking-[0.22em]" style={{ color: 'rgba(125,208,255,0.55)' }}>$GFG (DEVNET)</p>
          <p className="text-base font-bold" style={{ color: '#a8def7' }}>
            {state.gfg == null ? '—' : Number(state.gfg.uiAmount).toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-[9px] tracking-[0.22em]" style={{ color: 'rgba(125,208,255,0.55)' }}>PLAYER PDA</p>
          <p className="text-base font-bold flex items-center gap-1.5" style={{ color: state.playerExists ? '#88e878' : 'rgba(168,146,255,0.85)' }}>
            {state.playerExists == null ? '—' : state.playerExists ? '✓ initialized' : 'unitialised'}
          </p>
        </div>
        <div>
          <p className="text-[9px] tracking-[0.22em]" style={{ color: 'rgba(125,208,255,0.55)' }}>NETWORK</p>
          <p className="text-base font-bold uppercase" style={{ color: '#a8def7' }}>devnet</p>
        </div>
      </div>

      {/* Explorer links row */}
      <div className="flex flex-wrap gap-2 mt-4 relative z-10">
        <a
          href={explorerAddr(walletAddress)}
          target="_blank" rel="noopener noreferrer"
          data-testid="profile-explorer-wallet"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-[0.18em] hover:brightness-125 transition"
          style={{
            fontFamily: "'Courier New', monospace",
            color: '#7dd0ff',
            background: 'rgba(8,18,28,0.85)',
            border: '1px solid rgba(125,208,255,0.35)',
          }}
        >wallet<ExternalLink className="w-2.5 h-2.5" /></a>
        {state.gfg?.ata && (
          <a
            href={explorerAddr(state.gfg.ata)}
            target="_blank" rel="noopener noreferrer"
            data-testid="profile-explorer-ata"
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-[0.18em] hover:brightness-125 transition"
            style={{
              fontFamily: "'Courier New', monospace",
              color: '#7dd0ff',
              background: 'rgba(8,18,28,0.85)',
              border: '1px solid rgba(125,208,255,0.35)',
            }}
          >$gfg account<ExternalLink className="w-2.5 h-2.5" /></a>
        )}
        {playerPdaStr && (
          <a
            href={explorerAddr(playerPdaStr)}
            target="_blank" rel="noopener noreferrer"
            data-testid="profile-explorer-player"
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-[0.18em] hover:brightness-125 transition"
            style={{
              fontFamily: "'Courier New', monospace",
              color: '#7dd0ff',
              background: 'rgba(8,18,28,0.85)',
              border: '1px solid rgba(125,208,255,0.35)',
            }}
          >player pda<ExternalLink className="w-2.5 h-2.5" /></a>
        )}
        <a
          href={explorerAddr(DEVNET.programId)}
          target="_blank" rel="noopener noreferrer"
          data-testid="profile-explorer-program"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] tracking-[0.18em] hover:brightness-125 transition"
          style={{
            fontFamily: "'Courier New', monospace",
            color: '#7dd0ff',
            background: 'rgba(8,18,28,0.85)',
            border: '1px solid rgba(125,208,255,0.35)',
          }}
        >program<ExternalLink className="w-2.5 h-2.5" /></a>
      </div>
      {/* Initialize Player CTA — when PDA doesn't exist yet */}
      {state.playerExists === false && (
        <div
          className="mt-4 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 relative z-10"
          data-testid="profile-init-player-block"
          style={{
            background: 'rgba(8,18,28,0.7)',
            border: '1px solid rgba(125,208,255,0.45)',
            borderLeft: '3px solid #7dd0ff',
          }}
        >
          <div className="flex-1 min-w-0">
            <p
              className="text-[10px] tracking-[0.32em] mb-0.5"
              style={{ color: '#a8def7', fontFamily: "'Cinzel', serif" }}
            >
              ⚡ FIRST · TIME
            </p>
            <p
              className="text-[12px]"
              style={{ color: 'rgba(168,222,247,0.85)', fontFamily: "'EB Garamond', serif", fontStyle: 'italic', lineHeight: 1.5 }}
            >
              Your on-chain Player account is uninitialized. Sign once to create it — you only do this once per wallet.
            </p>
            {initState.err && (
              <p
                data-testid="profile-init-player-error"
                className="text-[10px] mt-1"
                style={{ color: '#ff8b6b', fontFamily: "'Courier New', monospace" }}
              >{initState.err}</p>
            )}
            {initState.status === 'success' && initState.sig && (
              <a
                href={`https://explorer.solana.com/tx/${initState.sig}?cluster=devnet`}
                target="_blank" rel="noopener noreferrer"
                data-testid="profile-init-player-success"
                className="inline-flex items-center gap-1 text-[10px] mt-1 hover:brightness-125"
                style={{ color: '#88e878', fontFamily: "'Courier New', monospace" }}
              >✓ initialized · view tx <ExternalLink className="w-2.5 h-2.5" /></a>
            )}
          </div>
          <button
            onClick={initializePlayer}
            disabled={initState.status === 'signing' || initState.status === 'confirming' || !publicKey}
            data-testid="profile-init-player-btn"
            className="inline-flex items-center gap-2 px-4 py-2 transition-all hover:brightness-125 active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              fontFamily: "'Cinzel', serif",
              color: '#7dd0ff',
              background: 'linear-gradient(180deg, rgba(20,40,60,0.85) 0%, rgba(8,20,30,0.95) 100%)',
              border: '1px solid rgba(125,208,255,0.55)',
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initState.status === 'signing' && '✎ SIGN IN WALLET…'}
            {initState.status === 'confirming' && '⏳ CONFIRMING…'}
            {initState.status === 'success' && '✓ INITIALIZED'}
            {(initState.status === 'idle' || initState.status === 'error') && 'INITIALIZE PLAYER'}
          </button>
        </div>
      )}

      <style>{`
        @keyframes gfg-live-pulse { 0%,100% { opacity: .55; transform: scale(.92);} 50% { opacity: 1; transform: scale(1.08);} }
      `}</style>
    </Panel>
  );
};

// ═══ GENESIS PASSPORT PANEL — awarded to pre-launch demo users ═══
const GenesisPassportPanel = ({ walletAddress }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!walletAddress) return;
    try {
      const r = await fetch(`${API_URL}/api/genesis/eligibility/${walletAddress}`);
      if (r.ok) setData(await r.json());
    } catch {}
    finally { setLoading(false); }
  }, [walletAddress]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleClaim = async () => {
    if (claiming || !walletAddress) return;
    setClaiming(true); setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/genesis/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ demo_wallet: walletAddress }),
      });
      const d = await r.json();
      if (d.success) { await fetchStatus(); }
      else { setErr(d.detail || 'Claim failed'); }
    } catch (e) { setErr(e.message || 'Claim failed'); }
    setClaiming(false);
  };

  const copyCode = () => {
    if (!data?.code) return;
    navigator.clipboard.writeText(data.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !data) return null;

  const progressPct = Math.min(100, Math.round((data.games_played / data.threshold) * 100));
  const remaining = Math.max(0, data.threshold - data.games_played);

  return (
    <Panel className="p-4 md:p-5 mb-6 relative overflow-hidden" glow data-testid="genesis-passport-panel">
      {/* Ambient glow */}
      <motion.div
        className="absolute -top-10 -right-10 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.25), transparent 70%)', filter: 'blur(30px)' }}
        animate={{ opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <motion.div animate={{ rotate: [-6, 6, -6] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}>
            <Crown className="w-4 h-4" style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 6px #fbbf24)' }} />
          </motion.div>
          <span className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#fbbf24' }}>
            Genesis Passport
          </span>
          <span className="text-[8px] px-1.5 py-0.5 font-bold" style={{ fontFamily: "'Courier New', monospace", background: 'rgba(168,85,247,0.2)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.4)' }}>
            PRE-LAUNCH
          </span>
        </div>

        {data.has_code ? (
          /* ─── CODE ISSUED ─── */
          <div className="relative">
            {/* Cinematic passport crystal art — decorative floating accent */}
            <motion.img
              src={passportCardArt}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="hidden md:block absolute -top-2 -right-2 w-28 pointer-events-none select-none"
              style={{ opacity: 0.55, mixBlendMode: 'screen', filter: 'drop-shadow(0 0 16px rgba(251,191,36,0.45))' }}
              initial={{ y: -4 }}
              animate={{ y: [-4, 4, -4], rotate: [-1.5, 1.5, -1.5] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <p className="text-xs md:text-sm mb-3 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,230,180,0.8)' }}>
              You are a Genesis Tester. Save this code — you'll use it at launch to claim a permanent on-chain badge on your real Solana wallet.
            </p>
            <button
              onClick={copyCode}
              data-testid="genesis-code-copy"
              className="w-full group relative flex items-center justify-between gap-3 px-4 py-3 mb-2 transition-all"
              style={{
                background: 'linear-gradient(90deg, rgba(251,191,36,0.12), rgba(255,140,0,0.12))',
                border: '1px solid rgba(251,191,36,0.4)',
                boxShadow: 'inset 0 0 14px rgba(251,191,36,0.08)',
              }}
            >
              <span className="text-base md:text-xl font-black tracking-[0.15em]" style={{ fontFamily: "'Courier New', monospace", color: '#ffd700', textShadow: '0 0 8px rgba(251,191,36,0.5)' }}>
                {data.code}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: copied ? '#4ade80' : 'rgba(251,191,36,0.7)' }}>
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </span>
            </button>
            <p className="text-[9px] md:text-[10px] tracking-wider leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,180,120,0.5)' }}>
              One-time use · Issued after {data.games_played_at_issue || data.games_played} games · Redeemable at token launch
            </p>
          </div>
        ) : data.eligible ? (
          /* ─── ELIGIBLE, NOT CLAIMED ─── */
          <div>
            <p className="text-xs md:text-sm mb-3" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,230,180,0.85)' }}>
              You've played {data.games_played} games — you qualify for a <strong style={{ color: '#fbbf24' }}>Genesis Tester</strong> passport. Claim your code below and save it for launch day.
            </p>
            <motion.button
              onClick={handleClaim}
              disabled={claiming}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              data-testid="genesis-claim-btn"
              className="w-full py-2.5 md:py-3 font-bold tracking-[0.2em] uppercase text-sm transition-all disabled:opacity-50"
              style={{
                fontFamily: "'Cinzel', serif",
                background: 'linear-gradient(180deg, rgba(251,191,36,0.22), rgba(168,130,0,0.4))',
                border: '1px solid rgba(251,191,36,0.6)',
                color: '#ffd700',
                boxShadow: '0 0 22px rgba(251,191,36,0.25), inset 0 0 14px rgba(251,191,36,0.1)',
                textShadow: '0 0 8px rgba(251,191,36,0.5)',
              }}
            >
              {claiming ? 'Issuing code...' : 'Claim My Genesis Code'}
            </motion.button>
            {err && <p className="mt-2 text-[10px]" style={{ color: '#ff4040', fontFamily: "'Courier New', monospace" }}>{err}</p>}
          </div>
        ) : (
          /* ─── NOT ELIGIBLE YET ─── */
          <div>
            <p className="text-xs md:text-sm mb-3" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,230,180,0.75)' }}>
              Play <strong style={{ color: '#fbbf24' }}>{remaining} more arcade {remaining === 1 ? 'game' : 'games'}</strong> to earn your Genesis Tester passport — redeemable for a permanent on-chain badge at launch.
            </p>
            <div className="relative h-5 overflow-hidden mb-2" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="h-full"
                style={{ background: 'linear-gradient(90deg, #fbbf24, #ff8c00)', boxShadow: '0 0 12px rgba(251,191,36,0.4)' }}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-[10px] font-bold tracking-[0.2em]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.9)', textShadow: '0 0 6px rgba(0,0,0,0.8)' }}>
                  {data.games_played} / {data.threshold} GAMES
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
};

// Ambient fireflies
const Fireflies = () => (
  <div className="fixed inset-0 pointer-events-none z-[1] overflow-hidden">
    {[...Array(15)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full"
        style={{
          left: `${8 + Math.random() * 84}%`,
          top: `${8 + Math.random() * 84}%`,
          width: 2 + Math.random() * 4,
          height: 2 + Math.random() * 4,
          background: i % 3 === 0 ? 'rgba(34,197,94,0.6)' : i % 3 === 1 ? 'rgba(255,184,0,0.5)' : 'rgba(0,255,255,0.4)',
          boxShadow: i % 3 === 0 ? '0 0 8px rgba(34,197,94,0.4)' : i % 3 === 1 ? '0 0 8px rgba(255,184,0,0.3)' : '0 0 8px rgba(0,255,255,0.3)',
        }}
        animate={{
          y: [0, -20 - Math.random() * 30, 0],
          x: [0, 10 - Math.random() * 20, 0],
          opacity: [0.1, 0.7, 0.1],
          scale: [0.8, 1.2, 0.8],
        }}
        transition={{
          duration: 5 + Math.random() * 5,
          repeat: Infinity,
          delay: Math.random() * 5,
          ease: 'easeInOut',
        }}
      />
    ))}
  </div>
);

const ProfilePage = () => {
  const navigate = useNavigate();
  const { publicKey, connected: walletConnected, disconnect } = useWallet();
  const { isDemo, demoWallet } = useDemo();
  const { isLive } = useAppMode();
  const { features } = useFeatureFlags();
  const connected = walletConnected || isDemo;
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : undefined);

  const [scrollY, setScrollY] = useState(0);
  const [profile, setProfile] = useState(null);
  const [arcadeStats, setArcadeStats] = useState(null);
  const [feeBalance, setFeeBalance] = useState(0);
  const [oracleData, setOracleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [activeBorder, setActiveBorder] = useState(() => {
    return localStorage.getItem('gfg_profile_border') || 'toxic_pulse';
  });
  const [showBorderPicker, setShowBorderPicker] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [heistHistory, setHeistHistory] = useState([]);
  const [historyTab, setHistoryTab] = useState('arcade');
  const [streakData, setStreakData] = useState(null);
  const [spBreakdown, setSpBreakdown] = useState(null);
  const [reputation, setReputation] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchData = useCallback(async () => {
    if (!walletAddress) { setLoading(false); return; }
    setLoading(true);
    try {
      const [profileRes, arcadeRes, oracleRes, airdropRes, balanceRes, historyRes, streakRes, spRes, repRes] = await Promise.all([
        fetch(`${API_URL}/api/profile/${walletAddress}`),
        fetch(`${API_URL}/api/arcade/stats/${walletAddress}`),
        fetch(`${API_URL}/api/price-oracle/${walletAddress}`),
        fetch(`${API_URL}/api/airdrop/status/${walletAddress}`),
        fetch(`${API_URL}/api/slots/user/${walletAddress}`),
        fetch(`${API_URL}/api/arcade/history/${walletAddress}`),
        fetch(`${API_URL}/api/player/streak/${walletAddress}`),
        fetch(`${API_URL}/api/seasons/sp-breakdown/${walletAddress}`),
        fetch(`${API_URL}/api/player/reputation/${walletAddress}`),
      ]);
      const profileData = await profileRes.json();
      const arcadeData = await arcadeRes.json();
      const oracleDataRes = await oracleRes.json();
      const airdropData = airdropRes.ok ? await airdropRes.json() : null;
      const balanceData = balanceRes.ok ? await balanceRes.json() : null;
      const historyData = historyRes.ok ? await historyRes.json() : null;
      const streakResult = streakRes.ok ? await streakRes.json() : null;
      const spResult = spRes.ok ? await spRes.json() : null;
      setProfile(profileData);
      setArcadeStats(arcadeData);
      setOracleData(oracleDataRes);
      if (airdropData) setReferralData(airdropData);
      if (balanceData) setFeeBalance(balanceData.fee_balance || 0);
      if (historyData) setHeistHistory(historyData.games || []);
      if (streakResult?.streak) setStreakData(streakResult.streak);
      if (spResult?.breakdown) setSpBreakdown(spResult.breakdown);
      const repResult = repRes.ok ? await repRes.json() : null;
      if (repResult?.reputation) setReputation(repResult.reputation);
      setNewUsername(profileData.username || '');
      if (profileData.avatar) {
        setSelectedAvatar(GREMLIN_AVATARS.find(a => a.id === profileData.avatar) || null);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const syncProfile = async () => {
    if (!walletAddress) return;
    setSyncing(true);
    try {
      await fetch(`${API_URL}/api/profile/${walletAddress}/sync`, { method: 'POST' });
      await fetchData();
    } catch (err) { console.error('Failed to sync:', err); }
    finally { setSyncing(false); }
  };

  const shareRank = () => {
    if (!walletAddress) return;
    // Open modal that shows the OG preview, share URL, and quick share actions.
    // No more silent navigator.share — always visible feedback.
    setShareCopied(false);
    setShowShareModal(true);
  };

  // Build derived share values (safe when modal is closed — values are just unused)
  const shareOrigin = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
  const shareUrl = walletAddress ? `${shareOrigin}/api/share/rank/${walletAddress}` : '';
  const shareImageUrl = walletAddress ? `${shareOrigin}/api/og/rank/${walletAddress}.png` : '';
  const shareText = `I took my seat in the Court of $GFG. Can you outrank me?\n\n🕯️ Demo is live. Token launches later. The sewer remembers who came first.`;
  const twitterIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const telegramIntent = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch (err) {
      // Fallback for older browsers / non-HTTPS contexts
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setShareCopied(true); setTimeout(() => setShareCopied(false), 2200); }
      catch { console.error('Clipboard failed:', err); }
      document.body.removeChild(ta);
    }
  };

  const saveUsername = async () => {
    if (!walletAddress || !newUsername) return;
    try {
      const res = await fetch(`${API_URL}/api/profile/${walletAddress}/username?username=${encodeURIComponent(newUsername)}`, { method: 'POST' });
      if (res.ok) { setProfile(prev => ({ ...prev, username: newUsername })); setEditingUsername(false); }
    } catch (err) { console.error('Failed to save username:', err); }
  };

  const saveAvatar = async (avatar) => {
    if (!walletAddress) return;
    try {
      const res = await fetch(`${API_URL}/api/profile/${walletAddress}/avatar?avatar_id=${avatar.id}`, { method: 'POST' });
      if (res.ok) { setSelectedAvatar(avatar); setProfile(prev => ({ ...prev, avatar: avatar.id })); setShowAvatarPicker(false); }
    } catch (err) { console.error('Failed to save avatar:', err); }
  };

  // Not connected state — Cinematic "Enter The Den" experience
  if (!connected && !isDemo && !isLive) {
    return (
      <div className="min-h-screen text-white relative overflow-hidden select-none" style={{ background: '#030304' }}>
        {/* Den BG with parallax — desktop only */}
        <div className="fixed inset-0 z-0 overflow-hidden hidden md:block">
          <motion.img
            src={SEWER_GATE_BG} alt=""
            className="w-full h-[130%] object-cover will-change-transform"
            style={{ transform: `translateY(${scrollY * -0.12}px) scale(1.08)`, filter: 'brightness(0.35) saturate(0.8)' }}
            initial={{ scale: 1.15, opacity: 0 }}
            animate={{ scale: 1.08, opacity: 1 }}
            transition={{ duration: 2.5, ease: [0.16, 1, 0.3, 1] }}
          />
          {/* Color grade overlay — warm amber/green tint */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(8,5,2,0.6) 40%, rgba(0,0,0,0.85) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(34,197,94,0.04) 0%, transparent 60%)' }} />
        </div>
        <div className="fixed inset-0 z-0 bg-[#030304] md:hidden" />

        {/* CRT Scanlines */}
        <div className="fixed inset-0 z-[2] pointer-events-none opacity-[0.03]" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255,255,255,0.05) 1px, rgba(255,255,255,0.05) 2px)',
          backgroundSize: '100% 3px',
        }} />

        {/* Film grain noise */}
        <div className="fixed inset-0 z-[2] pointer-events-none opacity-[0.025]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }} />

        {/* Vignette */}
        <div className="fixed inset-0 z-[2] pointer-events-none" style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)',
        }} />

        {/* Atmospheric fog at bottom */}
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-[3] pointer-events-none h-[40vh]"
          style={{ background: 'linear-gradient(to top, rgba(34,197,94,0.06) 0%, rgba(34,197,94,0.02) 30%, transparent 100%)' }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Drifting embers */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={`ember-${i}`}
            className="fixed rounded-full z-[3] pointer-events-none"
            style={{
              left: `${5 + Math.random() * 90}%`,
              bottom: '-2%',
              width: 1.5 + Math.random() * 2,
              height: 1.5 + Math.random() * 2,
              background: i % 4 === 0 ? '#22c55e' : i % 4 === 1 ? '#fbbf24' : i % 4 === 2 ? 'rgba(255,120,50,0.8)' : 'rgba(0,255,200,0.6)',
              boxShadow: `0 0 ${4 + Math.random() * 4}px ${i % 2 === 0 ? 'rgba(34,197,94,0.5)' : 'rgba(255,180,50,0.4)'}`,
            }}
            animate={{
              y: [0, -(window.innerHeight * (0.5 + Math.random() * 0.5))],
              x: [0, (Math.random() - 0.5) * 80],
              opacity: [0, 0.8, 0.6, 0],
              scale: [0.5, 1, 0.3],
            }}
            transition={{
              duration: 6 + Math.random() * 8,
              repeat: Infinity,
              delay: Math.random() * 8,
              ease: 'easeOut',
            }}
          />
        ))}

        <Fireflies />

        {/* Back button */}
        <BackButton to="/" />

        {/* ═══ CENTRAL PORTAL ═══ */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center min-h-screen px-6">

          {/* Pulsing portal ring behind logo */}
          <motion.div
            className="absolute"
            style={{
              width: 280, height: 280,
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg, rgba(34,197,94,0.0), rgba(34,197,94,0.12), rgba(0,255,180,0.08), rgba(34,197,94,0.0), rgba(168,85,247,0.06), rgba(34,197,94,0.12), rgba(0,255,180,0.08), rgba(34,197,94,0.0))',
              filter: 'blur(1px)',
            }}
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute"
            style={{
              width: 240, height: 240,
              borderRadius: '50%',
              border: '1px solid rgba(34,197,94,0.08)',
              boxShadow: '0 0 40px rgba(34,197,94,0.05), inset 0 0 40px rgba(34,197,94,0.03)',
            }}
            animate={{
              boxShadow: [
                '0 0 40px rgba(34,197,94,0.05), inset 0 0 40px rgba(34,197,94,0.03)',
                '0 0 80px rgba(34,197,94,0.12), inset 0 0 60px rgba(34,197,94,0.06)',
                '0 0 40px rgba(34,197,94,0.05), inset 0 0 40px rgba(34,197,94,0.03)',
              ],
              scale: [1, 1.02, 1],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          {/* Outer orbit ring */}
          <motion.div
            className="absolute"
            style={{
              width: 320, height: 320,
              borderRadius: '50%',
              border: '1px solid rgba(34,197,94,0.04)',
            }}
            animate={{ rotate: [0, -360] }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          >
            {/* Orbiting rune dots */}
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <motion.div
                key={`rune-${i}`}
                className="absolute rounded-full"
                style={{
                  width: 3, height: 3,
                  background: i % 2 === 0 ? 'rgba(34,197,94,0.5)' : 'rgba(168,85,247,0.4)',
                  boxShadow: `0 0 6px ${i % 2 === 0 ? 'rgba(34,197,94,0.4)' : 'rgba(168,85,247,0.3)'}`,
                  top: '50%', left: '50%',
                  transform: `rotate(${deg}deg) translateY(-160px) translateX(-1.5px)`,
                }}
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2 + i * 0.3, repeat: Infinity, ease: 'easeInOut' }}
              />
            ))}
          </motion.div>

          {/* Gremlin Logo — floating with ethereal glow */}
          <motion.div
            className="relative mb-6"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ filter: 'blur(30px)', background: 'radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 70%)' }}
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.img
              src={GREMLIN_LOGO} alt="GFG"
              className="w-28 h-28 relative z-10 drop-shadow-[0_0_20px_rgba(34,197,94,0.3)]"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>

          {/* Title — staggered letter reveal */}
          <motion.div
            className="relative mb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold tracking-[0.08em] text-center uppercase" style={{
              fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
              background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(34,197,94,0.7) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: 'none',
              filter: 'drop-shadow(0 0 20px rgba(34,197,94,0.15))',
            }}>
              The Den Awaits
            </h1>
            {/* Decorative line under title */}
            <motion.div
              className="mx-auto mt-3"
              style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(34,197,94,0.3), rgba(168,85,247,0.2), rgba(34,197,94,0.3), transparent)' }}
              initial={{ width: 0 }}
              animate={{ width: 200 }}
              transition={{ delay: 1.2, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            />
          </motion.div>

          {/* Subtitle — typed feel */}
          <motion.p
            className="text-sm font-mono tracking-wider text-center mb-10 max-w-xs"
            style={{ color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
          >
            Only those who hold the key may enter the lair
          </motion.p>

          {/* Wallet Connect — ritual-style button */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Glow behind button */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{ filter: 'blur(20px)', background: 'rgba(34,197,94,0.1)' }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
            {features.wallet_connect ? (
              <WalletMultiButton className="!bg-transparent hover:!bg-emerald-500/10 !border !border-emerald-500/25 hover:!border-emerald-400/50 !rounded-full !px-10 !py-3.5 !font-mono !font-bold !text-emerald-400 !text-sm !tracking-widest !uppercase !transition-all !duration-300 !shadow-[0_0_30px_rgba(34,197,94,0.08)] hover:!shadow-[0_0_40px_rgba(34,197,94,0.15)]" />
            ) : (
              <p className="text-xs tracking-wider" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(34,197,94,0.3)' }}>Wallet connection coming at launch</p>
            )}
          </motion.div>

          {/* Bottom decorative rune marks */}
          <motion.div
            className="absolute bottom-8 flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.5 }}
          >
            <div className="w-8 h-[1px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(34,197,94,0.15))' }} />
            <p className="text-[9px] font-mono tracking-[0.3em] uppercase" style={{ color: 'rgba(255,255,255,0.1)' }}>
              v1.0 // sewer protocol
            </p>
            <div className="w-8 h-[1px]" style={{ background: 'linear-gradient(270deg, transparent, rgba(34,197,94,0.15))' }} />
          </motion.div>

          {/* Corner brackets — cinematic framing */}
          <div className="fixed top-5 left-5 w-8 h-8 border-l border-t z-20 pointer-events-none" style={{ borderColor: 'rgba(34,197,94,0.08)' }} />
          <div className="fixed top-5 right-5 w-8 h-8 border-r border-t z-20 pointer-events-none" style={{ borderColor: 'rgba(34,197,94,0.08)' }} />
          <div className="fixed bottom-5 left-5 w-8 h-8 border-l border-b z-20 pointer-events-none" style={{ borderColor: 'rgba(34,197,94,0.08)' }} />
          <div className="fixed bottom-5 right-5 w-8 h-8 border-r border-b z-20 pointer-events-none" style={{ borderColor: 'rgba(34,197,94,0.08)' }} />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen text-white flex items-center justify-center relative">
        <div className="fixed inset-0"><div className="hidden md:block w-full h-full"><img src={SEWER_GATE_BG} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-black/70" /></div><div className="md:hidden absolute inset-0 bg-[#0a0a0a]" /></div>
        <motion.div className="relative z-10 w-10 h-10 border-2 border-emerald-500/40 border-t-emerald-400 rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} />
      </div>
    );
  }

  const isAdmin = walletAddress === ADMIN_WALLET;
  const tierInfo = isAdmin 
    ? { tier: 'mythic', title: 'Mythic Gremlin' } 
    : (profile?.holder_tier || { tier: 'none', title: 'Newcomer' });
  const totalFees = arcadeStats?.total_fees || 0;
  const oraclePoints = oracleData?.points || 0;
  const currentAvatar = selectedAvatar || GREMLIN_AVATARS.find(a => a.id === profile?.avatar) || GREMLIN_AVATARS[0];
  const feeMasterTitle = isAdmin ? FEE_MASTER_TITLES[FEE_MASTER_TITLES.length - 1] : getFeeMasterTitle(totalFees);
  const isGoldenCatcher = walletAddress ? localStorage.getItem(`gfg_golden_caught_${walletAddress}`) === 'true' : false;

  const slotStats = arcadeStats?.slot_stats || {};
  const whackStats = arcadeStats?.whack_stats || {};
  const safeStats = arcadeStats?.safe_stats || {};
  const oraclePredictions = oracleData?.total_predictions || 0;
  const oracleAccuracy = oracleData?.accuracy || 0;

  const currentTierIdx = TIER_CONFIG.findIndex(t => t.tier === tierInfo.tier);
  const nextTier = currentTierIdx < TIER_CONFIG.length - 1 ? TIER_CONFIG[currentTierIdx + 1] : null;

  const totalGames = (slotStats.total_spins || 0) + (whackStats.games_played || 0) + (safeStats.total_attempts || 0);

  return (
    <div className="min-h-screen text-white relative" data-testid="profile-page"
      style={{ background: '#020e06' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      {/* Background */}
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #0a1f10 0%, #061209 40%, #020e06 100%)' }} />
      {/* Sewer gate background — faded */}
      <div className="fixed inset-0 z-0 hidden md:block">
        <img src={SEWER_GATE_BG} alt="" className="w-full h-full object-cover" style={{ opacity: 0.15, filter: 'brightness(0.8) saturate(0.7)' }} />
      </div>
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 25%, rgba(34,197,94,0.06) 0%, transparent 50%)' }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 4, repeat: Infinity }} />
      </div>
      {/* CRT */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.18) 50%)', backgroundSize: '100% 4px', opacity: 0.1,
      }} />
      {/* Particles */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <motion.div key={`p-${i}`} className="absolute rounded-full"
            style={{
              left: `${5 + i * 10}%`, bottom: '-8px',
              width: `${2 + Math.random() * 2}px`, height: `${2 + Math.random() * 2}px`,
              background: i % 2 === 0 ? '#4ade80' : '#22c55e',
              boxShadow: `0 0 ${6 + Math.random() * 4}px rgba(34,197,94,0.6)`,
            }}
            animate={{ y: [0, -600], x: [0, (Math.random() - 0.5) * 25], opacity: [0, 0.6, 0.2, 0] }}
            transition={{ duration: 6 + Math.random() * 4, delay: i * 0.6, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
      </div>

      <BackButton to="/" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-16">

        {/* ═══ HEADER ═══ */}
        <div className="mb-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="relative">
              <div className="absolute -inset-x-6 -inset-y-3 pointer-events-none" style={{
                background: 'radial-gradient(ellipse at 40% 60%, rgba(34,197,94,0.08) 0%, transparent 70%)', filter: 'blur(12px)',
              }} />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1 relative"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(74,222,128,0.6)' }}>
                Gremlin Identity
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
                className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
                style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="profile-title">
                <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(34,197,94,0.2)', filter: 'blur(6px)' }}>THE DEN</span>
                <span className="relative" style={{
                  background: 'linear-gradient(180deg, #d1fae5 0%, #86efac 15%, #4ade80 30%, #22c55e 50%, #16a34a 70%, #15803d 90%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 25px rgba(34,197,94,0.5)) drop-shadow(0 0 50px rgba(34,197,94,0.2))',
                }}>THE DEN</span>
                <motion.span className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(209,250,229,0.4) 50%, transparent 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
                  animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>THE DEN</motion.span>
              </motion.h1>
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2">
              <motion.button onClick={shareRank}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-wider transition-all relative overflow-hidden"
                style={{ fontFamily: "'Cinzel', serif", border: '1px solid rgba(250,204,21,0.35)', background: 'linear-gradient(180deg, rgba(220,38,38,0.12), rgba(124,29,29,0.18))', color: '#facc15', boxShadow: '0 0 16px rgba(250,204,21,0.08) inset' }}
                whileHover={{ borderColor: 'rgba(253,224,71,0.7)', boxShadow: '0 0 24px rgba(250,204,21,0.25) inset, 0 0 20px rgba(220,38,38,0.25)' }}
                whileTap={{ scale: 0.97 }}
                data-testid="profile-share-rank-btn">
                <Share2 className="w-3 h-3" />
                SHARE MY RANK
              </motion.button>
              <motion.button onClick={syncProfile} disabled={syncing}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-wider transition-all"
                style={{ fontFamily: "'Cinzel', serif", border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.05)', color: '#4ade80' }}
                whileHover={{ borderColor: 'rgba(34,197,94,0.4)' }} data-testid="profile-sync-btn">
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing' : 'Sync'}
              </motion.button>
              <motion.button onClick={disconnect}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-wider transition-all"
                style={{ fontFamily: "'Cinzel', serif", border: '1px solid rgba(239,68,68,0.15)', background: 'rgba(239,68,68,0.04)', color: 'rgba(239,68,68,0.5)' }}
                data-testid="profile-disconnect-btn">
                <LogOut className="w-3 h-3" />
              </motion.button>
            </div>
          </div>
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #22c55e, #4ade80, #22c55e, transparent)' }} />
        </div>

        {/* ═══ IDENTITY CARD ═══ */}
        <Panel glow className="p-6 mb-6" data-testid="profile-identity-card">
          <div className="flex items-center gap-6 relative z-10">
            {/* Avatar */}
            <div className="relative flex-shrink-0 cursor-pointer" onClick={() => setShowAvatarPicker(true)} data-testid="profile-avatar">
              <ProfileBorder borderId={activeBorder} size={112}>
                <div className="w-[88px] h-[88px] rounded-full overflow-hidden" style={{ background: '#0a0a0f' }}>
                  <img src={currentAvatar.imageUrl} alt="" className="w-full h-full object-cover object-top" />
                </div>
              </ProfileBorder>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center z-10"
                style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <Edit2 className="w-3 h-3" style={{ color: '#4ade80' }} />
              </div>
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                {editingUsername ? (
                  <div className="flex items-center gap-2">
                    <input value={newUsername} onChange={e => setNewUsername(e.target.value)} maxLength={20}
                      className="bg-transparent text-xl font-bold outline-none"
                      style={{ fontFamily: "'Cinzel', serif", color: '#4ade80', borderBottom: '1px solid rgba(34,197,94,0.3)', width: '180px' }}
                      autoFocus data-testid="username-input" />
                    <button onClick={saveUsername}><Check className="w-4 h-4" style={{ color: '#22c55e' }} /></button>
                    <button onClick={() => setEditingUsername(false)}><X className="w-4 h-4" style={{ color: 'rgba(239,68,68,0.5)' }} /></button>
                  </div>
                ) : (
                  <button onClick={() => setEditingUsername(true)} className="flex items-center gap-2 group" data-testid="username-edit-trigger">
                    <h2 className="text-xl sm:text-2xl font-bold" style={{
                      fontFamily: profile?.equipped_font ? (ORACLE_FONTS.find(f => f.id === profile.equipped_font)?.family || "'Cinzel', serif") : "'Cinzel', serif",
                      color: profile?.equipped_font ? (ORACLE_FONTS.find(f => f.id === profile.equipped_font)?.color || '#d1fae5') : '#d1fae5',
                    }}>{profile?.username || walletAddress?.slice(0, 8)}</h2>
                    <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" style={{ color: '#4ade80' }} />
                  </button>
                )}
              </div>
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
                  style={{ fontFamily: "'Cinzel', serif", background: TIER_STYLES[tierInfo.tier].color + '15', color: TIER_STYLES[tierInfo.tier].color, border: '1px solid ' + TIER_STYLES[tierInfo.tier].color + '30' }}>
                  {tierInfo.title || TIER_STYLES[tierInfo.tier].name}
                </span>
                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
                  style={{ fontFamily: "'Cinzel', serif", background: feeMasterTitle.color + '15', color: feeMasterTitle.color, border: '1px solid ' + feeMasterTitle.color + '30' }}>
                  {feeMasterTitle.title}
                </span>
              </div>
              <p className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.35)' }}>{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</p>
            </div>
          </div>
        </Panel>

        {/* ═══ GENESIS PASSPORT — demo only ═══ */}
        {isDemo && <GenesisPassportPanel walletAddress={walletAddress} />}

        {/* ═══ LIVE MODE — on-chain balances (devnet) ═══ */}
        {isLive && walletConnected && walletAddress && (
          <LiveBalancesPanel walletAddress={walletAddress} Panel={Panel} />
        )}

        {/* ═══ THREE KEY STATS ═══ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="profile-key-stats">
          {[
            { label: 'Fees Earned', value: totalFees.toLocaleString(), icon: Coins, color: '#22c55e' },
            { label: 'Fee Balance', value: feeBalance.toLocaleString(), icon: Zap, color: '#fbbf24' },
            { label: 'Season Points', value: (spBreakdown?.total_sp || 0).toLocaleString(), icon: Trophy, color: '#ff6b00' },
          ].map(s => (
            <Panel key={s.label} className="p-4">
              <div className="flex items-center gap-2 mb-2 relative z-10">
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color, filter: `drop-shadow(0 0 3px ${s.color}60)` }} />
                <span className="text-[9px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(74,222,128,0.45)' }}>{s.label}</span>
              </div>
              <p className="text-2xl font-bold relative z-10" style={{ fontFamily: "'Impact', 'Arial Black', sans-serif", color: s.color, filter: `drop-shadow(0 0 4px ${s.color}40)` }}>{s.value}</p>
            </Panel>
          ))}
        </div>

        {/* ═══ STREAK + SP BREAKDOWN — 2 column ═══ */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {/* Streak */}
          <Panel className="p-4" data-testid="profile-streak">
            <div className="flex items-center gap-3 relative z-10">
              <motion.div
                animate={streakData?.current > 0 ? { scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] } : {}}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
                <Flame className="w-8 h-8" style={{
                  color: streakData?.current >= 20 ? '#ff4400' : streakData?.current >= 10 ? '#ff6b00' : streakData?.current >= 5 ? '#f59e0b' : '#6b7280',
                  filter: streakData?.current >= 5 ? 'drop-shadow(0 0 6px rgba(255,107,0,0.4))' : 'none',
                }} />
              </motion.div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,107,0,0.6)' }}>The Chain</p>
                <p className="text-xl font-bold" style={{ fontFamily: "'Impact', sans-serif", color: streakData?.current > 0 ? '#ff6b00' : 'rgba(255,255,255,0.15)' }}>
                  {streakData?.current || 0} day{streakData?.current !== 1 ? 's' : ''}
                </p>
                <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.3)' }}>
                  +{streakData?.bonus_pct || 0}% SP bonus // best: {streakData?.best || 0}
                </p>
              </div>
            </div>
            <div className="mt-3 h-2 overflow-hidden relative z-10" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,107,0,0.1)' }}>
              <motion.div className="h-full" style={{ background: 'linear-gradient(90deg, #b45309, #ff6b00)' }}
                initial={{ width: 0 }} animate={{ width: `${Math.min(100, ((streakData?.current || 0) / 30) * 100)}%` }}
                transition={{ duration: 1, ease: 'easeOut' }} />
            </div>
          </Panel>

          {/* SP Breakdown */}
          <Panel className="p-4" data-testid="profile-sp-breakdown">
            <p className="text-[10px] tracking-[0.12em] uppercase font-bold mb-3 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.6)' }}>SP Breakdown</p>
            <div className="space-y-2 relative z-10">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#22c55e' }}>Arcade</span>
                <span className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#22c55e' }}>{(spBreakdown?.arcade_sp || 0).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#ff6b00' }}>Burns</span>
                <span className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#ff6b00' }}>{(spBreakdown?.burn_sp || 0).toLocaleString()}</span>
              </div>
              <div className="h-2 overflow-hidden flex" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.08)' }}>
                {(spBreakdown?.total_sp || 0) > 0 && (<>
                  <div style={{ width: `${(spBreakdown.arcade_sp / spBreakdown.total_sp) * 100}%`, background: '#22c55e' }} className="h-full" />
                  <div style={{ width: `${(spBreakdown.burn_sp / spBreakdown.total_sp) * 100}%`, background: '#ff6b00' }} className="h-full" />
                </>)}
              </div>
              <p className="text-[8px]" style={{ fontFamily: "'Courier New', monospace", color: spBreakdown?.skill_floor_active ? 'rgba(239,68,68,0.5)' : 'rgba(74,222,128,0.25)' }}>
                {spBreakdown?.skill_floor_active ? 'burn cap reached. play more to unlock' : `burn cap: ${(spBreakdown?.burn_cap_remaining || 0).toLocaleString()} SP left`}
              </p>
            </div>
          </Panel>
        </div>

        {/* ═══ QUICK STATS ═══ */}
        <Panel className="px-5 py-3 mb-6">
          <div className="flex items-center justify-between relative z-10">
            {[
              { label: 'Games', value: totalGames, color: '#a855f7' },
              { label: 'Oracle', value: oraclePoints, color: '#06b6d4' },
              { label: 'Referrals', value: referralData?.referral_count || 0, color: '#f59e0b' },
              { label: 'Accuracy', value: oracleAccuracy + '%', color: '#22d3ee' },
            ].map(s => (
              <div key={s.label} className="text-center px-2">
                <p className="text-base font-bold" style={{ fontFamily: "'Impact', sans-serif", color: s.color }}>{s.value}</p>
                <p className="text-[8px] uppercase tracking-wider font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(74,222,128,0.3)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </Panel>

        {/* ═══ SEWER LEGENDS — Reputation ═══ */}
        <Panel className="p-5 mb-6" data-testid="profile-reputation">
          <div className="flex items-center gap-2 mb-4 relative z-10">
            <Award className="w-4 h-4" style={{ color: '#a78bfa', filter: 'drop-shadow(0 0 3px rgba(167,139,250,0.4))' }} />
            <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#a78bfa' }}>Sewer Legends</span>
            <span className="ml-auto text-lg font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#a855f7' }}>{reputation?.prs || 0} PRS</span>
          </div>
          {reputation && reputation.seasons_played > 0 ? (
            <div className="relative z-10">
              <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
                {reputation.season_history.slice(0, 8).map((h, i) => {
                  const rc = { mythic: '#ff00ff', diamond: '#00bfff', platinum: '#00ffff', gold: '#ffd700', silver: '#c0c0c0', bronze: '#cd7f32', iron: '#6b7280' };
                  return (
                    <div key={i} className="flex-shrink-0 w-14 p-2 text-center" style={{ background: (rc[h.rank] || '#6b7280') + '10', border: `1px solid ${(rc[h.rank] || '#6b7280')}20` }}>
                      <p className="text-[8px] font-bold" style={{ fontFamily: "'Impact', sans-serif", color: rc[h.rank] || '#6b7280' }}>{h.rank?.slice(0, 3).toUpperCase()}</p>
                      <p className="text-[7px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>S{h.season_number || i + 1}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mb-3">
                <div className="text-center"><p className="text-xs font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#ffd700' }}>{reputation.peak_rank?.toUpperCase()}</p><p className="text-[8px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>Peak</p></div>
                <div className="text-center"><p className="text-xs font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#22c55e' }}>{reputation.seasons_played}</p><p className="text-[8px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>Seasons</p></div>
              </div>
              {reputation.titles_earned.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {reputation.titles_earned.map(t => (
                    <span key={t.id} className="px-2 py-1 text-[8px] font-bold uppercase tracking-wider"
                      style={{ fontFamily: "'Cinzel', serif", background: (t.color || '#6b7280') + '12', color: t.color || '#6b7280', border: `1px solid ${t.color || '#6b7280'}20` }}>{t.title}</span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 relative z-10">
              <p className="text-xs" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.35)' }}>No seasons completed yet</p>
            </div>
          )}
        </Panel>

        {/* ═══ REFERRAL LINK ═══ */}
        {referralData?.referral_code && (
          <Panel className="p-4 mb-6" data-testid="profile-referral-link">
            <div className="flex items-center gap-2 mb-2 relative z-10">
              <Link className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
              <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#f59e0b' }}>Referral Link</span>
            </div>
            <div className="flex items-center gap-2 relative z-10">
              <div className="flex-1 px-3 py-2 text-xs truncate"
                style={{ fontFamily: "'Courier New', monospace", background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(245,158,11,0.1)', color: 'rgba(74,222,128,0.4)' }}>
                {window.location.origin}/airdrop?ref={referralData.referral_code}
              </div>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={() => navigator.clipboard.writeText(window.location.origin + '/airdrop?ref=' + referralData.referral_code)}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold"
                style={{ fontFamily: "'Cinzel', serif", background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b' }}
                data-testid="copy-referral-btn">
                <Copy className="w-3.5 h-3.5" /> Copy
              </motion.button>
            </div>
          </Panel>
        )}

        {/* ═══ HOLDER TIERS ═══ */}
        <Panel className="p-5 mb-6" data-testid="profile-tiers">
          <div className="flex items-center gap-2 mb-4 relative z-10">
            <Crown className="w-4 h-4" style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 3px rgba(251,191,36,0.4))' }} />
            <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#fbbf24' }}>Holder Tiers</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 relative z-10">
            {TIER_CONFIG.map((t, idx) => {
              const isCurrent = t.tier === tierInfo.tier;
              const isUnlocked = idx <= currentTierIdx;
              return (
                <div key={t.tier} className="p-3 relative overflow-hidden transition-all"
                  style={{
                    background: isCurrent ? t.color + '10' : 'rgba(0,0,0,0.25)',
                    border: isCurrent ? '1px solid ' + t.color + '35' : '1px solid rgba(34,197,94,0.06)',
                    opacity: isUnlocked ? 1 : 0.3,
                  }}>
                  {isCurrent && <motion.div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: t.color }} animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} />}
                  <div className="flex items-center gap-1.5 mb-1">
                    {isUnlocked ? <t.icon className="w-3 h-3" style={{ color: t.color }} /> : <Lock className="w-3 h-3" style={{ color: 'rgba(74,222,128,0.1)' }} />}
                    <p className="text-xs font-bold" style={{ fontFamily: "'Cinzel', serif", color: isUnlocked ? t.color : 'rgba(74,222,128,0.15)' }}>{t.name}</p>
                  </div>
                  <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.25)' }}>Hold {t.holdings} $GFG</p>
                  <p className="text-[8px] mt-0.5" style={{ fontFamily: "'Courier New', monospace", color: isUnlocked ? t.color + '60' : 'rgba(74,222,128,0.1)' }}>{t.perk}</p>
                </div>
              );
            })}
          </div>
          {nextTier && (
            <div className="mt-3 pt-3 flex items-center gap-2 text-xs relative z-10" style={{ borderTop: '1px solid rgba(34,197,94,0.06)' }}>
              <Zap className="w-3 h-3" style={{ color: '#fbbf24' }} />
              <span style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.35)' }}>Next: <span className="font-bold" style={{ color: nextTier.color }}>{nextTier.name}</span> at {nextTier.holdings} $GFG</span>
            </div>
          )}
        </Panel>

        {/* ═══ GAME HISTORY ═══ */}
        {heistHistory.length > 0 && (
          <Panel className="mb-6 overflow-hidden">
            <details>
              <summary className="p-4 cursor-pointer flex items-center gap-2 relative z-10" data-testid="game-history-toggle">
                <Clock className="w-4 h-4" style={{ color: 'rgba(34,197,94,0.4)' }} />
                <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(74,222,128,0.5)' }}>Game History ({heistHistory.length})</span>
                <ChevronRight className="w-3 h-3 ml-auto" style={{ color: 'rgba(74,222,128,0.25)' }} />
              </summary>
              <div className="px-4 pb-4 space-y-1 relative z-10">
                {heistHistory.slice(0, 15).map((game, i) => {
                  const gc = { safe_cracker: { icon: Crosshair, label: 'Safe Cracker', color: '#06b6d4' }, whack_a_gremlin: { icon: Hammer, label: 'Snatch-a-Bag', color: '#22c55e' }, slot_machine: { icon: Dices, label: 'Fee Scrambler', color: '#f59e0b' }, price_prediction: { icon: Target, label: 'Price Oracle', color: '#06b6d4' }, the_descent: { icon: Skull, label: 'The Descent', color: '#ef4444' } };
                  const cfg = gc[game.game_type] || gc.safe_cracker;
                  const Icon = cfg.icon;
                  const fees = game.fees_earned || 0;
                  let t = '';
                  if (game.played_at) { const d = Date.now() - new Date(game.played_at).getTime(); const m = Math.floor(d / 60000); t = m < 60 ? m + 'm' : m < 1440 ? Math.floor(m / 60) + 'h' : Math.floor(m / 1440) + 'd'; }
                  return (
                    <div key={i} className="flex items-center gap-3 px-3 py-2" style={{ background: i % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'transparent' }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: cfg.color + '60' }} />
                      <span className="text-xs flex-1" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.5)' }}>{cfg.label}</span>
                      <span className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>{t}</span>
                      <span className="text-xs font-bold" style={{ fontFamily: "'Impact', sans-serif", color: fees > 0 ? cfg.color : 'rgba(239,68,68,0.4)' }}>{fees > 0 ? '+' + fees.toLocaleString() : '0'}</span>
                    </div>
                  );
                })}
              </div>
            </details>
          </Panel>
        )}

        {/* ═══ BORDERS ═══ */}
        <Panel className="mb-6 overflow-hidden">
          <details>
            <summary className="p-4 cursor-pointer flex items-center gap-2 relative z-10" data-testid="borders-toggle">
              <Sparkles className="w-4 h-4" style={{ color: 'rgba(167,139,250,0.5)' }} />
              <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(167,139,250,0.5)' }}>Profile Borders</span>
              <ChevronRight className="w-3 h-3 ml-auto" style={{ color: 'rgba(74,222,128,0.2)' }} />
            </summary>
            <div className="px-4 pb-4 relative z-10">
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                {PROFILE_BORDERS.map(border => {
                  const isActive = activeBorder === border.id;
                  const isAdminUser = walletAddress === ADMIN_WALLET;
                  const isUnlocked = isAdminUser || (() => { switch (border.unlock) { case 'free': return true; case 'games_100': return (arcadeStats?.games_played || 0) >= 100; case 'fees_100k': return totalFees >= 100000; case 'burn_10k': return (spBreakdown?.tokens_burned || 0) >= 10000; case 'streak_14': return (streakData?.best || 0) >= 14; case 'rank_gold': return reputation?.season_history?.some(h => ['gold', 'platinum', 'diamond', 'mythic'].includes(h.rank)); case 'oracle_50w_75a': { const w = oracleData?.total_wins || 0; return w >= 50 && (oracleData?.accuracy || 0) >= 75; } case 'oracle_150w_85a_25s': { return (oracleData?.total_wins || 0) >= 150 && (oracleData?.accuracy || 0) >= 85 && (oracleData?.best_streak || 0) >= 25; } case 'oracle_500w_90a_50s': { return (oracleData?.total_wins || 0) >= 500 && (oracleData?.accuracy || 0) >= 90 && (oracleData?.best_streak || 0) >= 50; } default: return false; } })();
                  return (
                    <motion.button key={border.id} whileHover={{ scale: isUnlocked ? 1.03 : 1 }} whileTap={{ scale: isUnlocked ? 0.97 : 1 }}
                      onClick={() => { if (isUnlocked) { setActiveBorder(border.id); localStorage.setItem('gfg_profile_border', border.id); }}}
                      className="p-3 text-center relative"
                      style={{ background: isActive ? (border.color || '#a855f7') + '10' : 'rgba(0,0,0,0.2)', border: isActive ? '1px solid ' + (border.color || '#a855f7') + '40' : '1px solid rgba(34,197,94,0.06)', opacity: isUnlocked ? 1 : 0.3, cursor: isUnlocked ? 'pointer' : 'not-allowed' }}>
                      <div className="mx-auto mb-2 flex items-center justify-center">
                        <ProfileBorder borderId={border.id} size={48}><div className="w-[36px] h-[36px] rounded-full" style={{ background: (border.color || '#a855f7') + '15' }} /></ProfileBorder>
                      </div>
                      <p className="text-[10px] font-bold" style={{ fontFamily: "'Cinzel', serif", color: isUnlocked ? (border.color || '#a855f7') : 'rgba(74,222,128,0.15)' }}>{border.name}</p>
                      <p className="text-[8px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>{border.howTo || border.desc}</p>
                      {isActive && <Check className="absolute top-1.5 right-1.5 w-3 h-3" style={{ color: border.color || '#a855f7' }} />}
                      {!isUnlocked && <Lock className="w-3 h-3 mx-auto mt-1" style={{ color: 'rgba(74,222,128,0.1)' }} />}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </details>
        </Panel>

        {/* ═══ ORACLE FONTS ═══ */}
        <Panel className="mb-6 overflow-hidden">
          <details>
            <summary className="p-4 cursor-pointer flex items-center gap-2 relative z-10" data-testid="fonts-toggle">
              <Type className="w-4 h-4" style={{ color: 'rgba(6,182,212,0.5)' }} />
              <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(6,182,212,0.5)' }}>Oracle Fonts</span>
              <ChevronRight className="w-3 h-3 ml-auto" style={{ color: 'rgba(74,222,128,0.2)' }} />
            </summary>
            <div className="px-4 pb-4 relative z-10">
              <p className="text-[10px] mb-3" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.3)' }}>Earn exclusive fonts through the Price Oracle mini-game</p>
              <div className="space-y-2">
                {ORACLE_FONTS.map(font => {
                  const isEquipped = profile?.equipped_font === font.id;
                  const isAdminUser = walletAddress === ADMIN_WALLET;
                  const isUnlocked = isAdminUser || ((oracleData?.total_wins || 0) >= font.requirements.wins && (oracleData?.accuracy || 0) >= font.requirements.accuracy && (oracleData?.best_streak || 0) >= font.requirements.streak);
                  const equipFont = async () => { if (!walletAddress || !isUnlocked) return; try { await fetch(`${API_URL}/api/profile/${walletAddress}/font?font_id=${font.id}`, { method: 'POST' }); setProfile(p => ({ ...p, equipped_font: font.id })); } catch {} };
                  const unequipFont = async () => { try { await fetch(`${API_URL}/api/profile/${walletAddress}/font?font_id=none`, { method: 'POST' }); setProfile(p => ({ ...p, equipped_font: null })); } catch {} };
                  return (
                    <div key={font.id} className="flex items-center gap-3 p-3"
                      style={{ background: isEquipped ? font.color + '08' : 'rgba(0,0,0,0.2)', border: isEquipped ? '1px solid ' + font.color + '25' : '1px solid rgba(34,197,94,0.04)', opacity: isUnlocked ? 1 : 0.3 }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate" style={{ fontFamily: font.family, color: isUnlocked ? font.color : 'rgba(74,222,128,0.15)' }}>{font.name}</p>
                        <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>{font.requirements.wins}W / {font.requirements.accuracy}% acc{font.requirements.streak > 0 ? ` / ${font.requirements.streak} streak` : ''}</p>
                      </div>
                      {isUnlocked ? (isEquipped ? (
                        <button onClick={unequipFont} className="px-3 py-1 text-[9px] font-bold" style={{ fontFamily: "'Cinzel', serif", background: font.color + '15', color: font.color, border: '1px solid ' + font.color + '30' }}>EQUIPPED</button>
                      ) : (
                        <button onClick={equipFont} className="px-3 py-1 text-[9px] font-bold" style={{ fontFamily: "'Cinzel', serif", background: 'rgba(34,197,94,0.05)', color: 'rgba(74,222,128,0.35)', border: '1px solid rgba(34,197,94,0.1)' }}>EQUIP</button>
                      )) : <Lock className="w-3 h-3 flex-shrink-0" style={{ color: 'rgba(74,222,128,0.1)' }} />}
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        </Panel>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(74,222,128,0.2)' }}>
            The Den remembers all who enter.
          </p>
        </div>
      </div>

      {/* Avatar Picker Modal */}
      <AnimatePresence>
        {showAvatarPicker && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
            onClick={() => setShowAvatarPicker(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-lg w-full p-6 rounded-2xl max-h-[80vh] overflow-y-auto"
              style={{ background: 'rgba(15,15,20,0.95)', border: '1px solid rgba(255,255,255,0.08)' }}
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-bold text-white/70">Choose Avatar</h3>
                <button onClick={() => setShowAvatarPicker(false)} className="text-white/20 hover:text-white/50"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {GREMLIN_AVATARS.map(avatar => (
                  <motion.button key={avatar.id} onClick={() => saveAvatar(avatar)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${
                      currentAvatar.id === avatar.id ? 'border-emerald-500 shadow-[0_0_15px_rgba(34,197,94,0.3)]' : 'border-white/5 hover:border-white/20'
                    }`}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid={`avatar-${avatar.id}`}>
                    <div className="w-full aspect-square bg-black/40 overflow-hidden">
                      <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="bg-black/80 px-1 py-1.5 border-t border-white/5">
                      <p className="text-[10px] text-center text-white/60 font-semibold truncate">{avatar.name}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ SHARE RANK MODAL ═══ */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowShareModal(false)}
            data-testid="share-rank-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto"
              style={{
                background: 'linear-gradient(180deg, #0a0405 0%, #140708 100%)',
                border: '2px solid #b8860b',
                outline: '1px solid #7c1d1d',
                outlineOffset: '-10px',
                boxShadow: '0 30px 80px rgba(220,38,38,.35), 0 0 0 1px #facc15 inset',
              }}
            >
              {/* Close */}
              <button
                onClick={() => setShowShareModal(false)}
                className="absolute top-3 right-3 z-10 p-2 transition-colors"
                style={{ color: '#facc15' }}
                data-testid="share-modal-close-btn"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-6 sm:p-8">
                {/* Header */}
                <p className="text-[10px] uppercase tracking-[0.4em] text-center" style={{ fontFamily: "'Cinzel', serif", color: '#dc2626' }}>
                  · Share · The Court of $GFG ·
                </p>
                <h3 className="text-2xl sm:text-3xl text-center mt-2 mb-5" style={{ fontFamily: "'Cinzel Decorative', serif", fontWeight: 700, color: '#fde047', textShadow: '0 0 20px rgba(220,38,38,0.6)' }}>
                  Show the Court
                </h3>

                {/* Quick share actions — ABOVE the fold, always visible */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => { window.open(twitterIntent, '_blank', 'noopener,noreferrer'); setShowShareModal(false); }}
                    className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold tracking-wider cursor-pointer"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      border: '2px solid #facc15',
                      backgroundColor: '#dc2626',
                      backgroundImage: 'linear-gradient(180deg, #dc2626, #7c1d1d)',
                      color: '#fde047',
                      boxShadow: '0 0 24px rgba(220,38,38,0.35)',
                      minHeight: '48px',
                    }}
                    data-testid="share-modal-twitter-btn"
                  >
                    <Share2 className="w-4 h-4" style={{ flexShrink: 0 }} />
                    <span>SHARE ON X</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { window.open(telegramIntent, '_blank', 'noopener,noreferrer'); setShowShareModal(false); }}
                    className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold tracking-wider cursor-pointer"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      border: '1px solid rgba(250,204,21,0.4)',
                      backgroundColor: 'rgba(15,5,6,0.95)',
                      color: '#facc15',
                      minHeight: '48px',
                    }}
                    data-testid="share-modal-telegram-btn"
                  >
                    <Share2 className="w-4 h-4" style={{ flexShrink: 0 }} />
                    <span>SHARE ON TELEGRAM</span>
                  </button>
                </div>

                {/* URL + Copy */}
                <div className="flex items-stretch gap-2 mb-5">
                  <input
                    readOnly
                    value={shareUrl}
                    onClick={(e) => e.target.select()}
                    className="flex-1 px-3 py-3 text-xs outline-none font-mono"
                    style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(250,204,21,0.25)', color: '#f5e6be' }}
                    data-testid="share-modal-url-input"
                  />
                  <button
                    onClick={copyShareUrl}
                    className="px-4 py-3 text-xs font-bold tracking-wider transition-all flex items-center gap-2"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      border: '1px solid rgba(250,204,21,0.5)',
                      background: shareCopied ? 'rgba(34,197,94,0.15)' : 'linear-gradient(180deg, rgba(220,38,38,0.18), rgba(124,29,29,0.25))',
                      color: shareCopied ? '#4ade80' : '#fde047',
                    }}
                    data-testid="share-modal-copy-btn"
                  >
                    {shareCopied ? <><Check className="w-4 h-4" /> COPIED</> : <><Copy className="w-4 h-4" /> COPY</>}
                  </button>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(250,204,21,0.3), transparent)' }} />
                  <span className="text-[10px] uppercase tracking-[0.3em]" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(245,230,190,0.5)' }}>Preview</span>
                  <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(250,204,21,0.3), transparent)' }} />
                </div>

                {/* Preview image */}
                <div className="relative border overflow-hidden mb-3" style={{ borderColor: '#b8860b', boxShadow: '0 0 40px rgba(220,38,38,0.15) inset' }}>
                  <img
                    src={shareImageUrl}
                    alt="Your Season Rank card"
                    className="w-full block"
                    style={{ aspectRatio: '1200 / 630', objectFit: 'cover', background: '#0a0405' }}
                    data-testid="share-modal-preview-img"
                  />
                </div>

                <p className="text-[10px] text-center tracking-[0.3em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(245,230,190,0.5)' }}>
                  The sewer remembers who came first
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ProfilePage;
